import { useState } from "react";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { useAuth } from "@/lib/auth-context";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { Coins, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PromoCodeField } from "@/components/PromoCodeField";
import { useFeeConfig } from "@/lib/fees";

const PACKAGES = [
  { id: "pkg5", price: 275, points: 5 },
  { id: "pkg10", price: 500, points: 10, popular: true },
  { id: "pkg18", price: 900, points: 18 },
  { id: "pkg25", price: 1200, points: 25 },
  { id: "pkg35", price: 1500, points: 35 },
];

export default function Points() {
  const { user } = useAuth();
  const [loadingId, setLoadingId] = useState(null);
  const [promo, setPromo] = useState("");
  const { transaction_fee_pct } = useFeeConfig();

  const buy = async (pkgId) => {
    setLoadingId(pkgId);
    try {
      const { data } = await api.post("/checkout/session", {
        type: "points_package",
        reference_id: pkgId,
        origin_url: window.location.origin,
        promo_code: promo || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoadingId(null);
    }
  };

  return (
    <ClientLayout>
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Points</div>
        <h1 className="font-display text-5xl md:text-6xl mt-2 flex items-center gap-4">
          <Coins weight="fill" className="text-[#FF2D2D]" size={56} />
          Top up.
        </h1>
        <p className="text-[#a3a3a3] mt-3 max-w-2xl">
          Stack points and use them like currency on flyers, logos, banners, and more.
        </p>
      </div>

      <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-5 mb-10" data-testid="balance-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Current balance</div>
            <div className="font-display text-5xl mt-1">{(user?.points_balance ?? 0).toFixed(2)} <span className="text-2xl text-[#FF2D2D]">pts</span></div>
          </div>
          <Coins weight="fill" size={48} className="text-[#FF2D2D]" />
        </div>
        <PromoCodeField value={promo} onChange={setPromo} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {PACKAGES.map((p) => (
          <div
            key={p.id}
            className={`relative p-6 border bg-[#0a0a0a] flex flex-col ${
              p.popular ? "border-[#FF2D2D] shadow-[0_0_25px_rgba(255,204,0,0.15)]" : "border-[#262626]"
            }`}
            data-testid={`points-pkg-${p.id}`}
          >
            {p.popular && (
              <div className="absolute -top-3 left-6 bg-[#FF2D2D] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                Most picked
              </div>
            )}
            <div className="font-display text-5xl text-[#FF2D2D]">{p.points}</div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#a3a3a3] mt-1">points</div>
            <div className="mt-6 pt-4 border-t border-[#262626] flex items-baseline justify-between">
              <span className="text-xs text-[#a3a3a3]">USD</span>
              <span className="font-display text-2xl">{formatUSD(p.price)}</span>
            </div>
            {transaction_fee_pct > 0 && (
              <div className="text-[10px] text-[#666] uppercase tracking-wider mt-1 text-right" data-testid={`points-fee-hint-${p.id}`}>
                + {Number(transaction_fee_pct).toFixed(1)}% at checkout ({formatUSD(p.price * transaction_fee_pct / 100)})
              </div>
            )}
            <button
              onClick={() => buy(p.id)}
              disabled={loadingId === p.id}
              data-testid={`buy-${p.id}`}
              className="btn-primary mt-5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2"
            >
              {loadingId === p.id ? "Loading…" : "Buy"} <ArrowUpRight size={14} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-12 border border-[#262626] bg-[#0a0a0a] p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">Point costs</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 text-sm">
          <div><span className="text-[#a3a3a3]">Flyer (digital)</span> · <strong>1 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Extra size</span> · <strong>0.25 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Print flyer setup</span> · <strong>2 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Social banner</span> · <strong>1 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Web banner</span> · <strong>1 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Print banner</span> · <strong>3 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Logo</span> · <strong>5 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Seal/Crest</span> · <strong>7 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Video edit</span> · <strong>3 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Advanced video</span> · <strong>5 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Extra revision</span> · <strong>0.5 pt</strong></div>
          <div><span className="text-[#a3a3a3]">Rush</span> · <strong>2–3 pt</strong></div>
        </div>
      </div>
    </ClientLayout>
  );
}
